import {
  ApplicationConfig,
  LOCALE_ID,
  provideZoneChangeDetection,
  APP_INITIALIZER,
  inject,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { mdTransitionAnimation } from '@ionic/angular';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { provideServiceWorker } from '@angular/service-worker';
import { provideFirebaseApp, initializeApp, FirebaseApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { provideRemoteConfig, getRemoteConfig, RemoteConfig } from '@angular/fire/remote-config';
import { provideStorage, getStorage } from '@angular/fire/storage';
import { provideFirestore } from '@angular/fire/firestore';
import { getFirestore, initializeFirestore } from 'firebase/firestore';
import { provideHttpClient, HTTP_INTERCEPTORS, withInterceptorsFromDi } from '@angular/common/http';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import {
  PwaUpdateService,
  ThemeService,
  ConfigService,
  LoadingInterceptor,
  LoadingService,
  TenantService,
  TenantSelectionService,
  SessionMemberService,
  WorkspaceInstanceService,
  WorkspaceSessionService,
  APP_SETTINGS_TOKEN,
  BOARD_LOCK_SERVICE_TOKEN,
  RECAPTCHA_SITE_KEY,
  PUBLIC_MARKETING_RUNTIME_CONFIG,
  type PublicMarketingRuntimeConfig,
} from '@crowdctrl/shared';
import { appThemeConfig } from '../theme.config';
import { AppSettingsService } from './services/app-settings.service';
import { BoardLockService } from './services/board-lock.service';

function initializePwaUpdateService(): () => void {
  return () => {
    const pwaUpdateService = inject(PwaUpdateService);
    pwaUpdateService.initialize();
  };
}

function initializeThemeService(): () => void {
  return () => {
    const themeService = inject(ThemeService);
    // Set default theme for this app
    const savedTheme = localStorage.getItem('crowdctrl-theme');
    if (!savedTheme) {
      themeService.setTheme(appThemeConfig.defaultTheme);
    }
  };
}

function initializeTenantService(): () => void {
  return () => {
    const tenantService = inject(TenantService);
    tenantService.setBaseUrl(environment.apiUrl);
  };
}

function initializeTenantSelectionService(): () => void {
  return () => {
    const tenantSelectionService = inject(TenantSelectionService);
    tenantSelectionService.setBaseUrl(environment.apiUrl);
  };
}

function initializeWorkspaceInstanceService(): () => void {
  return () => {
    const workspaceInstanceService = inject(WorkspaceInstanceService);
    workspaceInstanceService.setBaseUrl(environment.apiUrl);
  };
}

function initializeWorkspaceSessionService(): () => void {
  return () => {
    const workspaceSessionService = inject(WorkspaceSessionService);
    workspaceSessionService.setBaseUrl(environment.apiUrl);
  };
}

function initializeSessionMemberService(): () => void {
  return () => {
    const sessionMemberService = inject(SessionMemberService);
    sessionMemberService.setBaseUrl(environment.apiUrl);
  };
}

function initializeConfigService(): () => void {
  return async () => {
    const configService = inject(ConfigService);
    try {
      // Get Remote Config instance
      const remoteConfig = inject(RemoteConfig);
      await configService.initializeWithRemoteConfig(remoteConfig);
    } catch (error) {
      // Fallback to defaults if Remote Config not available
      console.warn('Remote Config not available, using defaults:', error);
      await configService.initialize();
    }
  };
}

// Check if running on localhost (disable service worker only on localhost)
function isLocalhost(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'en-AU' },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),
    provideIonicAngular({
      navAnimation: mdTransitionAnimation,
    }),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: () => {
        const loadingService = inject(LoadingService);
        return new LoadingInterceptor(loadingService);
      },
      multi: true,
      deps: [LoadingService],
    },
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    provideRemoteConfig(() => getRemoteConfig()),
    provideStorage(() => getStorage()),
    provideFirestore(() => {
      const app = inject(FirebaseApp);
      // Non-production builds (dev/staging hosting): Firestore WebChannel through the Angular SW
      // can surface bogus CORS on error bodies. Long-polling avoids that path while keeping SW on hosting.
      if (environment.production) {
        return getFirestore(app);
      }
      return initializeFirestore(app, {
        experimentalForceLongPolling: true,
      });
    }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isLocalhost(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: initializePwaUpdateService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeThemeService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTenantService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTenantSelectionService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeWorkspaceInstanceService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeWorkspaceSessionService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeSessionMemberService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeConfigService,
      multi: true,
    },
    {
      provide: APP_SETTINGS_TOKEN,
      useExisting: AppSettingsService,
    },
    {
      provide: BOARD_LOCK_SERVICE_TOKEN,
      useExisting: BoardLockService,
    },
    {
      provide: RECAPTCHA_SITE_KEY,
      useValue: environment.recaptchaSiteKey ?? '',
    },
    {
      provide: PUBLIC_MARKETING_RUNTIME_CONFIG,
      useValue: {
        apiUrl: (environment.apiUrl ?? '').trim(),
        appName: environment.appName,
        appSubtitle: environment.appSubtitle,
        contactEmail: (environment.contactEmail ?? '').trim(),
        registerInterestUrl: (environment.registerInterestUrl ?? '').trim(),
        environmentName: environment.name,
        production: environment.production,
      } satisfies PublicMarketingRuntimeConfig,
    },
  ],
};
